import { gql, useMutation } from "@apollo/client";
import React, { useCallback, useState } from "react";
import { useToken } from "src/components/authentication/TokenProvider";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Form,
  FormLayout,
  Icon,
  Layout,
  LegacyCard,
  Text,
  BlockStack,
  Link,
} from "@shopify/polaris";
import { CustomersMinor, HideMinor, PasskeyMinor, ViewMinor } from "@shopify/polaris-icons";
import TextField from "src/components/shopify/form/TextField";
import { Graph } from "src/generated/graph";
import { CreateApolloClient } from "src/lib/ApolloClient";
import { useHistory } from "react-router";
import { useResendSupplierOtpMutation } from "src/graphql/codegen_graphql";
import { Toast } from "src/hooks/toast";

const LOGIN_MERCHANT = gql`
  mutation loginSupplier($username: String!, $password: String!) {
    loginSupplier(username: $username, password: $password) {
      success
      token
      message
      status
      useDeliveryService
    }
  }
`;

async function verifyToken(token: string) {
  const { data } = await CreateApolloClient(token).query({
    query: gql`
      query {
        merchant {
          id
          name
          logo
        }
      }
    `,
  });

  return data.merchant;
}

export function LoginScreen() {
  const history = useHistory();
  const { setToken } = useToken();
  const [userInput, setUserInput] = useState("");
  const [passwordInput, setPasswordInput] = useState("");
  const [status, setStatus] = useState(false);
  const [useTokenLogin, setUseTokenLogin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error_msg, setErrorMsg] = useState("");
  const [l, setl] = useState(false);
  const [items, setItems] = useState(JSON.parse(localStorage.getItem("recent_logins") || "[]"));

  const [loginSupplier, { loading, error }] = useMutation<Graph.Mutation>(LOGIN_MERCHANT, {
    refetchQueries: ["*"],
  });

  const [resendSupplierOtp] = useResendSupplierOtpMutation();

  const handleChangeUserInput = useCallback((value: any) => {
    setUserInput(value);
  }, []);

  const handleChangePasswordInput = useCallback((value: any) => {
    setPasswordInput(value);
  }, []);

  const handleSubmit = useCallback(
    (e: any) => {
      e.preventDefault();
      if (useTokenLogin) {
        setl(true);
        verifyToken("mx" + passwordInput)
          .then((res) => {
            if (res === null) {
              setErrorMsg("Token was incorrect.");
            } else {
              const x = [...items];
              if (!x.find((f) => f.id === res.id)) {
                if (x.length > 3) {
                  x.splice(0, 1);
                }
                x.push({
                  id: res.id,
                  name: res.name,
                  logo: res.logo,
                  username: res.name,
                  by: "TOKEN",
                  secret: passwordInput,
                });
              }
              setItems(x);
              localStorage.setItem("recent_logins", JSON.stringify(x));
              setToken("mx" + passwordInput);
              setTimeout(() => {
                window.location.reload();
              });
            }
            setl(false);
          })
          .catch(() => {
            setErrorMsg("Token was incorrect.");
            setl(false);
          });
        return;
      }

      loginSupplier({
        variables: {
          username: String(userInput),
          password: String(passwordInput),
        },
      }).then((result) => {
        const success = result.data?.loginSupplier?.success;
        const status = result.data?.loginSupplier?.status;

        Toast.onShow(result.data?.loginSupplier?.message || "", success ? "success" : "danger", "right");

        if (success === true && status === "NOT_VERIFY") {
          resendSupplierOtp({
            variables: {
              verifyToken: result.data?.loginSupplier?.token || "",
              verifyForgeted: false,
            },
          }).then((res) => {
            if (res.data?.resendSupplierOtp?.status) {
              history.push({
                pathname: "/verify",
                search: "passkey=" + res.data.resendSupplierOtp.token,
              });
            }
          });
        }

        if (success === true && status === "REVIEWED") {
          setStatus(true);
          verifyToken("mx" + result.data?.loginSupplier?.token).then((res) => {
            const x = [...items];
            if (!x.find((f) => f.id === res.id)) {
              if (x.length > 3) {
                x.splice(0, 1);
              }
              x.push({
                id: res.id,
                name: res.name,
                logo: res.logo,
                username: res.name,
                by: "AUTH",
                secret: result.data?.loginSupplier?.token,
              });
            }
            setItems(x);
            localStorage.setItem("recent_logins", JSON.stringify(x));
            setToken("mx" + result.data?.loginSupplier?.token);
            setTimeout(() => {
              window.location.reload();
            });
          });
        }

        if (success === false && status === "VERIFIED") {
          history.push({
            pathname: "/update/seller",
            search: "passkey=" + result.data?.loginSupplier?.token,
          });
        }
      });
    },
    [loginSupplier, passwordInput, setToken, useTokenLogin, userInput, items, resendSupplierOtp, history],
  );

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <BlockStack align="center" inlineAlign="center">
          <Layout>
            <Layout.Section>
              <LegacyCard>
                <LegacyCard.Section>
                  <Text as="h2" fontWeight="regular" variant="headingXl" tone="critical" alignment="center">
                    Welcome to L192 seller
                  </Text>
                  <Text as="p" variant="headingSm" alignment="center" tone="critical" fontWeight="regular">
                    Join the business with l192
                  </Text>
                </LegacyCard.Section>
                <LegacyCard.Section>
                  <Text as="p" variant="bodyMd">
                    Recent logins
                  </Text>
                  <div style={{ display: "flex", flexWrap: "wrap" }} className="flex flex-wrap">
                    {items
                      .filter((item: any) => !!item.id)
                      .map((item: any, index: number) => {
                        return (
                          <div key={index} className="mr-4 mt-4">
                            <Box shadow="300" borderRadius="100">
                              <div style={{ position: "relative", width: 125 }}>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    cursor: "pointer",
                                    flexDirection: "column",
                                  }}
                                  className="flex flex-col items-center p-4 hover:text-red-500 cursor-pointer"
                                  onClick={() => {
                                    setToken("mx" + (item as any).secret);
                                    window.location.reload();
                                  }}
                                >
                                  <div
                                    style={{
                                      borderRadius: "50%",
                                      width: 40,
                                      height: 40,
                                      objectFit: "contain",
                                    }}
                                    className="rounded-full w-[40px] h-[40px] object-contain mb-2"
                                  >
                                    <Avatar initials={item.name.charAt(0)} size="md" source={item.logo} />
                                  </div>
                                  <div style={{ width: 100 }} className="w-[100px]">
                                    <Text as="p" variant="bodySm" alignment="center" truncate>
                                      {item.name}
                                    </Text>
                                  </div>
                                </div>
                                <div className="absolute top-2 right-2 z-10">
                                  {/* <MenuPopover
                                    items={[
                                      {
                                        text: "Delete",
                                        onClick: () => {
                                          const items: any[] = recent.getLocalStorage("recent_logins") || [];
                                          setItems(items.filter((x) => x.id !== item.id));
                                          recent.setLocalStorage(
                                            "recent_logins",
                                            items.filter((x) => x.id !== item.id),
                                          );
                                        },
                                      },
                                    ]}
                                  /> */}
                                </div>
                              </div>
                            </Box>
                          </div>
                        );
                      })}
                  </div>
                </LegacyCard.Section>
                <LegacyCard.Section>
                  <Form onSubmit={handleSubmit} preventDefault={true}>
                    {useTokenLogin ? (
                      <TextField
                        required={!!useTokenLogin}
                        autoComplete="off"
                        label="Secret Token"
                        placeholder=""
                        type={showPassword ? "text" : "password"}
                        value={passwordInput}
                        requiredIndicator
                        onChange={handleChangePasswordInput}
                        prefix={<Icon source={PasskeyMinor as any} />}
                        suffix={
                          <div className="cursor-pointer" onClick={() => setShowPassword(!showPassword)}>
                            <Icon source={showPassword ? (HideMinor as any) : (ViewMinor as any)} />
                          </div>
                        }
                        error={!!error_msg}
                        helpText={!!error_msg && error_msg}
                        disabled={l}
                      />
                    ) : (
                      <div style={{ display: "flex", flexWrap: "wrap", flexDirection: 'row', justifyContent: 'space-between' }}>
                        <div style={{ width: '45%' }}><TextField
                          required={!useTokenLogin}
                          autoComplete="off"
                          label="Username"
                          placeholder="Enter username..."
                          value={userInput}
                          onChange={handleChangeUserInput}
                          requiredIndicator
                          prefix={<Icon source={CustomersMinor as any} />}
                          error={!!error}
                          helpText={error?.message}
                        /></div>
                        <div className="pl-2 pr-2"></div>
                        <div style={{ width: '45%' }}><TextField
                          required={!useTokenLogin}
                          autoComplete="off"
                          label="Password"
                          placeholder="Enter password..."
                          type={showPassword ? "text" : "password"}
                          value={passwordInput}
                          requiredIndicator
                          onChange={handleChangePasswordInput}
                          prefix={<Icon source={PasskeyMinor as any} />}
                          suffix={
                            <div className="cursor-pointer" onClick={() => setShowPassword(!showPassword)}>
                              <Icon source={showPassword ? (HideMinor as any) : (ViewMinor as any)} />
                            </div>
                          }
                          error={!!error}
                          helpText={error?.message}
                        /></div>
                      </div>
                    )}
                    <div className="mt-4" style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Checkbox
                        label="Use token login"
                        checked={!!useTokenLogin}
                        onChange={(value) => {
                          setUseTokenLogin(value);
                          setPasswordInput("");
                          setUserInput("");
                          setErrorMsg("");
                        }}
                      />

                      <div>
                        <Link url="#" onClick={() => {
                          history.push('/forget');
                          setTimeout(() => {
                            window.location.reload()
                          }, 100)
                        }}>Forget password?</Link>
                      </div>
                    </div>
                    <FormLayout>
                      <div className="mt-4">
                        <Button
                          fullWidth
                          variant="primary"
                          submit
                          loading={loading || status || l}
                          disabled={loading || status || l}
                          size="large"
                        >
                          Sing in
                        </Button>
                      </div>
                    </FormLayout>
                    <FormLayout>
                      <div className="mt-4">
                        <Text as="p" variant="bodyMd" alignment="center">
                          Don't have an account? <Link url="#" onClick={() => {
                            history.push('/signup');
                            setTimeout(() => {
                              window.location.reload()
                            }, 100)
                          }}>Request Now</Link>
                        </Text>
                      </div>
                    </FormLayout>
                  </Form>
                </LegacyCard.Section>
              </LegacyCard>
            </Layout.Section>
          </Layout>
        </BlockStack>
      </div>
    </div>
  );
}
